import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Loader } from '@mantine/core';
import type { MouseEvent } from 'react';
import { useState } from 'react';
import { getChatDocument } from '../../../../../api/agents-client/agent-client';
import { isSafeUrl } from '../../../../../helpers/general-helpers';
import { newCOLORS } from '../../../../../styles/colors';
import { Text } from '../../../../../ui-library/typography/typography';
import { DOC_TYPE_CONFIG, formatFileSize, isPreviewableDocType } from '../../flora-document/flora-document.helpers';
import type { Document } from '../../../flora.type';

interface AgentDocumentCardProps {
  document: Document;
  /**
   * Opens the document in the preview side panel. When provided and the type is
   * previewable (Word/Excel/PDF), clicking the card body previews rather than
   * downloads; the download icon always downloads regardless.
   */
  onPreviewDocument?: (documentId: string) => void;
}

/**
 * Inline card for a Flora-generated document, with two modes: clicking the card
 * body opens a previewable document (Word/Excel/PDF) in the preview side panel,
 * while a persistent download icon always downloads the file. Non-previewable
 * types (or when no preview handler is wired) download on a body click too.
 *
 * @param {AgentDocumentCardProps} props - The document to render and the optional preview handler
 * @returns {JSX.Element} The document card
 */
export function AgentDocumentCard({ document, onPreviewDocument }: AgentDocumentCardProps) {
  const config = DOC_TYPE_CONFIG[document.doc_type] || DOC_TYPE_CONFIG.pdf;
  const [isLoading, setIsLoading] = useState(false);
  // Preview only when the type supports it, a handler is wired, and we have an
  // id to preview by. Otherwise the whole card falls back to downloading.
  const canPreview = isPreviewableDocType(document.doc_type) && !!onPreviewDocument && !!document.document_id;

  const openDocument = async () => {
    if (document.document_id) {
      setIsLoading(true);
      try {
        const freshDoc = await getChatDocument(document.document_id);
        if (freshDoc?.download_url && isSafeUrl(freshDoc.download_url)) {
          window.open(freshDoc.download_url, '_blank', 'noopener,noreferrer');
        } else if (document.download_url && isSafeUrl(document.download_url)) {
          window.open(document.download_url, '_blank', 'noopener,noreferrer');
        }
      } catch {
        if (document.download_url && isSafeUrl(document.download_url)) {
          window.open(document.download_url, '_blank', 'noopener,noreferrer');
        }
      } finally {
        setIsLoading(false);
      }
    } else if (document.download_url && isSafeUrl(document.download_url)) {
      window.open(document.download_url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCardClick = () => {
    if (isLoading) return;
    if (canPreview) {
      onPreviewDocument?.(document.document_id);
    } else {
      openDocument();
    }
  };

  const handleDownload = (e: MouseEvent) => {
    e.stopPropagation();
    if (!isLoading) openDocument();
  };

  return (
    <Card
      onClick={handleCardClick}
      onKeyDown={(e) => {
        // Only the card itself activates on Enter/Space. Without this, a
        // keydown bubbling up from the nested download button would also fire
        // the card action (double-fire), and Space would preventDefault the
        // button's own activation — so a keyboard download would preview instead.
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
      aria-label={canPreview ? `Preview ${document.filename}` : `Download ${document.filename}`}
      tabIndex={0}
      role="button"
    >
      <IconBox $color={config.color}>
        <Icon icon={config.icon} width={28} height={28} />
      </IconBox>
      <FileInfo>
        <FileNameText size="small" weight="medium" color="var(--ink-dark)" title={document.filename}>
          {document.filename}
        </FileNameText>
        <FileMeta>
          <TypeLabel $color={config.color} size="tiny" weight="medium">
            {config.label}
          </TypeLabel>
          {document.file_size != null && (
            <Text size="tiny" color="var(--ink-lighter)">
              {formatFileSize(document.file_size)}
            </Text>
          )}
        </FileMeta>
      </FileInfo>
      <DownloadBtn
        type="button"
        onClick={handleDownload}
        disabled={isLoading}
        aria-label={`Download ${document.filename}`}
        title="Download"
      >
        {isLoading ? (
          <Loader size="xs" color={newCOLORS.darkGray} />
        ) : (
          <Icon icon="material-symbols:download" width={18} height={18} />
        )}
      </DownloadBtn>
    </Card>
  );
}

const Card = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  width: 210px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: var(--sky-base);
    box-shadow: 0 2px 6px var(--flora-shadow-card);
  }
`;

const IconBox = styled.div<{ $color: string }>`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Doc-type tint at ~7% alpha (the '12' hex suffix). */
  background: ${({ $color }) => `${$color}12`};
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const FileNameText = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TypeLabel = styled(Text)<{ $color: string }>`
  color: ${({ $color }) => $color};
`;

const FileMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DownloadBtn = styled.button`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--ink-lighter);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: var(--sky-lighter);
    color: var(--ink-dark);
  }

  &:disabled {
    cursor: default;
  }
`;
